import { create } from 'zustand';
import * as Tone from 'tone';
import { 
  syncThemeToBackend, 
  syncLayoutToBackend, 
  syncCustomThemesToBackend, 
  syncCustomLayoutsToBackend,
  loadPreferencesFromBackend 
} from '../services/preferences';
import {
  conflictDetector,
  resolveConflict,
  createConflictNotification,
  requiresManualResolution,
  type ConflictEvent,
  type EditOperation,
  type ConflictType,
} from '../services/conflictResolution';

// Types
export type NoteEvent = {
  id: string;
  time: number; // in beats
  pitch: number; // MIDI number
  duration: number; // in beats
  velocity: number; // 0-1
  channel: string;
};

export type StepSequencerPattern = {
  id: string;
  name: string;
  steps: boolean[][]; // [channel][step]
  velocities: number[][]; // [channel][step]
  length: number; // 16, 32, or 64
};

export type Channel = {
  id: string;
  name: string;
  type: 'sample' | 'synth';
  sampleUrl?: string;
  volume: number;
  pan: number;
  muted: boolean;
  solo: boolean;
  color: string;
  mixerTrackId: string;
};

export type PatternClip = {
  id: string;
  patternId: string;
  trackId: string;
  startTime: number; // in beats
  duration: number; // in beats
  color: string;
};

export type AudioClip = {
  id: string;
  url: string;
  trackId: string;
  startTime: number;
  duration: number;
  offset: number; // trim start
};

export type TimelineTrack = {
  id: string;
  name: string;
  height: number;
  color: string;
};

export type MixerTrack = {
  id: string;
  name: string;
  volume: number; // dB
  pan: number; // -1 to 1
  muted: boolean;
  solo: boolean;
  effects: Effect[];
  eqLow: number;
  eqMid: number;
  eqHigh: number;
  sendA: number; // 0-1
  sendB: number; // 0-1
  stereoWidth: number; // 0-2
  delayCompensation: number; // Delay compensation in samples
};

export type Effect = {
  id: string;
  type: 'eq' | 'reverb' | 'delay' | 'compressor' | 'distortion' | 'chorus' | 'phaser' | 'flanger' | 'pitchShift' | 'autoFilter' | 'tremolo';
  enabled: boolean;
  params: Record<string, number>;
  latency?: number; // Plugin latency in samples
};

export type AutomationPoint = {
  time: number; // in beats
  value: number;
};

export type AutomationLane = {
  id: string;
  trackId: string;
  parameterId: string;
  parameterName: string;
  points: AutomationPoint[];
  color: string;
};

export type CommentMarker = {
  id: string;
  time: number; // in beats
  text: string;
  author: string;
  timestamp: number; // unix timestamp
  resolved: boolean;
};

export type CollaborativeCursor = {
  userId: string;
  userName: string;
  color: string;
  position: { x: number; y: number };
  view: 'channel-rack' | 'playlist' | 'piano-roll' | 'mixer';
  lastUpdate: number;
};

export type ProjectSnapshot = {
  id: string;
  name: string;
  timestamp: number;
  state: any; // Full project state
  description?: string;
};

export type Theme = {
  id: string;
  name: string;
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
    elevated: string;
    border: string;
    borderLight: string;
    accentPrimary: string;
    accentHover: string;
    accentActive: string;
  };
};

export type WorkspaceLayout = {
  id: string;
  name: string;
  browserVisible: boolean;
  mixerHeight: number;
  channelRackHeight: number;
  savedPanelSizes: Record<string, number>;
};

export type Sample = {
  id: string;
  name: string;
  url: string;
  duration: number;
  tags: string[];
  waveform?: number[]; // Cached waveform data
};

export type Tool = 'select' | 'draw' | 'paint' | 'erase' | 'slice';

export type PlayMode = 'pattern' | 'song';

export type GridSize = 'line' | 'cell' | '1/4' | '1/8' | '1/16' | '1/32' | '1/64';

// Macro Controls Types
export type MacroParameter = {
  parameterId: string;
  parameterLabel: string;
  min: number;
  max: number;
};

export type Macro = {
  id: string;
  name: string;
  value: number; // 0-1
  parameters: MacroParameter[];
};

interface HistoryState {
  past: any[];
  future: any[];
}

interface DAWState {
  // Transport
  isPlaying: boolean;
  isRecording: boolean;
  bpm: number;
  playMode: PlayMode;
  loopEnabled: boolean;
  loopStart: number; // in beats
  loopEnd: number; // in beats
  currentTime: number; // in seconds
  metronomeEnabled: boolean;
  
  // Plugin Delay Compensation
  delayCompensationEnabled: boolean;
  maxPluginLatency: number; // Maximum latency across all tracks in samples
  
  // Grid & Snap
  gridSize: GridSize;
  snapEnabled: boolean;
  swing: number; // 0-1
  
  // Automation
  automationLanes: AutomationLane[];
  automationRecording: boolean;
  selectedAutomationLane: string | null;
  
  // Collaboration
  collaborativeCursors: CollaborativeCursor[];
  comments: CommentMarker[];
  projectSnapshots: ProjectSnapshot[];
  currentUserId: string;
  currentUserName: string;
  currentUserColor: string;
  
  // Conflict Resolution (Requirements 11.1, 11.2, 11.3)
  conflicts: ConflictEvent[];
  conflictNotifications: string[];
  
  // Theme & Workspace
  currentTheme: Theme;
  themes: Theme[];
  currentLayout: WorkspaceLayout;
  layouts: WorkspaceLayout[];
  
  // UI Panel State (for layout capture)
  browserVisible: boolean;
  mixerHeight: number;
  channelRackHeight: number;
  panelSizes: Record<string, number>;
  
  // Animation settings (Requirements 14.4, 14.5)
  reduceMotion: boolean;
  
  // Auto-save settings (Requirements 14.1, 14.2, 14.3, 14.4, 14.5)
  autoSaveEnabled: boolean;
  autoSaveInterval: number; // in milliseconds (default: 30 minutes)
  lastAutoSaveTime: number | null;
  editingStartTime: number | null;
  
  // Project
  projectId: string | null;
  projectName: string;
  lastSaved: number | null;
  
  // Channel Rack
  channels: Channel[];
  currentPattern: StepSequencerPattern;
  patterns: StepSequencerPattern[];
  
  // Playlist
  timelineTracks: TimelineTrack[];
  patternClips: PatternClip[];
  audioClips: AudioClip[];
  pixelsPerBeat: number;
  currentTool: Tool;
  
  // Piano Roll
  pianoRollNotes: NoteEvent[];
  pianoRollChannel: string | null;
  pianoRollScale: string; // e.g., "C Major"
  showGhostNotes: boolean;
  
  // Mixer
  mixerTracks: MixerTrack[];
  selectedMixerTrack: string | null;
  masterVolume: number;
  
  // Browser
  sampleLibrary: Sample[];
  searchQuery: string;
  selectedTags: string[];
  
  // System
  cpuLoad: number;
  latency: number;
  
  // Macros
  macros: Macro[];
  
  // History
  history: HistoryState;
  
  // Actions
  setPlaying: (playing: boolean) => void;
  setRecording: (recording: boolean) => void;
  setBpm: (bpm: number) => void;
  setPlayMode: (mode: PlayMode) => void;
  setLoopEnabled: (enabled: boolean) => void;
  setLoopPoints: (start: number, end: number) => void;
  setCurrentTime: (time: number) => void;
  toggleMetronome: () => void;
  
  setGridSize: (size: GridSize) => void;
  toggleSnap: () => void;
  setSwing: (swing: number) => void;
  
  addChannel: (channel: Omit<Channel, 'id'>) => void;
  updateChannel: (id: string, updates: Partial<Channel>) => void;
  removeChannel: (id: string) => void;
  toggleChannelMute: (id: string) => void;
  toggleChannelSolo: (id: string) => void;
  
  toggleStep: (channelIndex: number, stepIndex: number) => void;
  setStepVelocity: (channelIndex: number, stepIndex: number, velocity: number) => void;
  setPatternLength: (length: number) => void;
  addPattern: (pattern: Omit<StepSequencerPattern, 'id'>) => void;
  selectPattern: (id: string) => void;
  
  addTimelineTrack: (track: Omit<TimelineTrack, 'id'>) => void;
  addPatternClip: (clip: Omit<PatternClip, 'id'>) => void;
  updatePatternClip: (id: string, updates: Partial<PatternClip>) => void;
  removePatternClip: (id: string) => void;
  addAudioClip: (clip: Omit<AudioClip, 'id'>) => void;
  updateAudioClip: (id: string, updates: Partial<AudioClip>) => void;
  setPixelsPerBeat: (pixels: number) => void;
  setCurrentTool: (tool: Tool) => void;
  
  addNote: (note: Omit<NoteEvent, 'id'>) => void;
  updateNote: (id: string, updates: Partial<NoteEvent>) => void;
  removeNote: (id: string) => void;
  setPianoRollChannel: (channelId: string | null) => void;
  setPianoRollScale: (scale: string) => void;
  toggleGhostNotes: () => void;
  
  updateMixerTrack: (id: string, updates: Partial<MixerTrack>) => void;
  addEffect: (trackId: string, effect: Omit<Effect, 'id'>) => void;
  removeEffect: (trackId: string, effectId: string) => void;
  updateEffect: (trackId: string, effectId: string, updates: Partial<Effect>) => void;
  setSelectedMixerTrack: (id: string | null) => void;
  setMasterVolume: (volume: number) => void;
  
  // Plugin Delay Compensation actions
  setDelayCompensationEnabled: (enabled: boolean) => void;
  updatePluginLatency: (trackId: string, effectId: string, latency: number) => void;
  recalculateDelayCompensation: () => void;
  
  addSample: (sample: Sample) => void;
  setSearchQuery: (query: string) => void;
  toggleTag: (tag: string) => void;
  
  updateSystemStats: (cpu: number, latency: number) => void;
  
  // Macro actions
  addMacro: (macro: Omit<Macro, 'id'>) => void;
  removeMacro: (id: string) => void;
  updateMacro: (id: string, value: number) => void;
  updateMacroName: (id: string, name: string) => void;
  addMacroParameter: (macroId: string, parameter: MacroParameter) => void;
  removeMacroParameter: (macroId: string, parameterId: string) => void;
  updateMacroParameter: (macroId: string, parameterId: string, updates: Partial<MacroParameter>) => void;
  
  // History actions
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  
  // Automation actions
  addAutomationLane: (lane: Omit<AutomationLane, 'id'>) => void;
  removeAutomationLane: (id: string) => void;
  addAutomationPoint: (laneId: string, point: AutomationPoint) => void;
  removeAutomationPoint: (laneId: string, time: number) => void;
  updateAutomationPoint: (laneId: string, time: number, value: number) => void;
  toggleAutomationRecording: () => void;
  setSelectedAutomationLane: (id: string | null) => void;
  
  // Collaboration actions
  updateCollaborativeCursor: (cursor: CollaborativeCursor) => void;
  removeCollaborativeCursor: (userId: string) => void;
  addComment: (comment: Omit<CommentMarker, 'id'>) => void;
  updateComment: (id: string, updates: Partial<CommentMarker>) => void;
  removeComment: (id: string) => void;
  createSnapshot: (name: string, description?: string) => void;
  loadSnapshot: (id: string) => void;
  deleteSnapshot: (id: string) => void;
  
  // Conflict Resolution actions (Requirements 11.1, 11.2, 11.3)
  registerEdit: (operation: Omit<EditOperation, 'userId' | 'userName' | 'timestamp'>) => void;
  handleRemoteEdit: (operation: EditOperation) => void;
  getRecentConflicts: () => ConflictEvent[];
  clearConflicts: () => void;
  dismissConflictNotification: (index: number) => void;
  
  // Theme actions
  setTheme: (theme: Theme) => void;
  addCustomTheme: (theme: Theme) => void;
  exportTheme: (themeId: string) => void;
  importTheme: (file: File) => Promise<void>;
  removeCustomTheme: (themeId: string) => void;
  
  // Workspace actions
  setLayout: (layout: WorkspaceLayout) => void;
  saveCurrentLayout: (name: string) => void;
  deleteLayout: (id: string) => void;
  
  // UI Panel State actions
  setBrowserVisible: (visible: boolean) => void;
  setMixerHeight: (height: number) => void;
  setChannelRackHeight: (height: number) => void;
  setPanelSize: (panelId: string, size: number) => void;
  
  // Animation settings actions
  setReduceMotion: (enabled: boolean) => void;
  
  // Auto-save actions (Requirements 14.1, 14.2, 14.3, 14.4, 14.5)
  setAutoSaveEnabled: (enabled: boolean) => void;
  setAutoSaveInterval: (interval: number) => void;
  startEditingTimer: () => void;
  resetEditingTimer: () => void;
  checkAutoSave: () => void;
  
  // Project actions
  setProjectName: (name: string) => void;
  saveProject: () => Promise<void>;
  loadProject: (projectId: string) => Promise<void>;
  
  // Preferences sync
  loadPreferencesFromBackend: () => Promise<void>;
}

const generateId = () => Math.random().toString(36).substring(2, 11);

// Initialize default pattern
const createDefaultPattern = (): StepSequencerPattern => ({
  id: generateId(),
  name: 'Pattern 1',
  steps: Array(8).fill(null).map(() => Array(16).fill(false)),
  velocities: Array(8).fill(null).map(() => Array(16).fill(0.8)),
  length: 16,
});

// Default themes
const defaultTheme: Theme = {
  id: 'default',
  name: 'FL Studio Dark',
  colors: {
    primary: '#0a0a0b',
    secondary: '#121214',
    tertiary: '#1a1a1d',
    elevated: '#202023',
    border: '#2a2a2e',
    borderLight: '#3a3a3e',
    accentPrimary: '#6366f1',
    accentHover: '#818cf8',
    accentActive: '#4f46e5',
  },
};

const blueTheme: Theme = {
  id: 'blue',
  name: 'Ocean Blue',
  colors: {
    primary: '#0a0e1a',
    secondary: '#0f1729',
    tertiary: '#1a2332',
    elevated: '#233044',
    border: '#2d3f5a',
    borderLight: '#3d4f6a',
    accentPrimary: '#3b82f6',
    accentHover: '#60a5fa',
    accentActive: '#2563eb',
  },
};

const purpleTheme: Theme = {
  id: 'purple',
  name: 'Purple Haze',
  colors: {
    primary: '#0e0a1a',
    secondary: '#171229',
    tertiary: '#221a32',
    elevated: '#302344',
    border: '#3f2d5a',
    borderLight: '#4f3d6a',
    accentPrimary: '#8b5cf6',
    accentHover: '#a78bfa',
    accentActive: '#7c3aed',
  },
};

// Default layouts
const defaultLayout: WorkspaceLayout = {
  id: 'default',
  name: 'Default',
  browserVisible: true,
  mixerHeight: 400,
  channelRackHeight: 300,
  savedPanelSizes: {},
};

const producerLayout: WorkspaceLayout = {
  id: 'producer',
  name: 'Producer',
  browserVisible: true,
  mixerHeight: 500,
  channelRackHeight: 350,
  savedPanelSizes: {},
};

const performerLayout: WorkspaceLayout = {
  id: 'performer',
  name: 'Performer',
  browserVisible: false,
  mixerHeight: 600,
  channelRackHeight: 200,
  savedPanelSizes: {},
};

// Initialize default mixer tracks
const createDefaultMixerTracks = (): MixerTrack[] => {
  return Array.from({ length: 8 }, (_, i) => ({
    id: `mixer-${i}`,
    name: `Track ${i + 1}`,
    volume: 0,
    pan: 0,
    muted: false,
    solo: false,
    effects: [],
    eqLow: 0,
    eqMid: 0,
    eqHigh: 0,
    sendA: 0,
    sendB: 0,
    stereoWidth: 1,
    delayCompensation: 0,
  }));
};

// Helper to get snapshot of state for history
const getSnapshot = (state: DAWState) => ({
  channels: state.channels,
  currentPattern: state.currentPattern,
  patterns: state.patterns,
  patternClips: state.patternClips,
  audioClips: state.audioClips,
  pianoRollNotes: state.pianoRollNotes,
  mixerTracks: state.mixerTracks,
});

// Helper to apply theme CSS variables
const applyThemeVariables = (theme: Theme) => {
  const root = document.documentElement;
  const colorMap: Record<string, string> = {
    primary: '--daw-bg-primary',
    secondary: '--daw-bg-secondary',
    tertiary: '--daw-bg-tertiary',
    elevated: '--daw-bg-elevated',
    border: '--daw-border',
    borderLight: '--daw-border-light',
    accentPrimary: '--daw-accent-primary',
    accentHover: '--daw-accent-hover',
    accentActive: '--daw-accent-active',
  };
  
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVar = colorMap[key];
    if (cssVar) {
      root.style.setProperty(cssVar, value);
    }
  });
};

// Helper to load theme from localStorage
const loadThemeFromStorage = (): Theme => {
  try {
    const stored = localStorage.getItem('music-legend-theme');
    if (stored) {
      const theme = JSON.parse(stored);
      // Apply theme to CSS variables immediately
      applyThemeVariables(theme);
      return theme;
    }
  } catch (error) {
    console.error('Failed to load theme from localStorage:', error);
  }
  // Apply default theme to CSS variables
  applyThemeVariables(defaultTheme);
  return defaultTheme;
};

// Helper to load custom themes from localStorage
const loadCustomThemes = (): Theme[] => {
  try {
    const stored = localStorage.getItem('music-legend-custom-themes');
    if (stored) {
      const customThemes = JSON.parse(stored);
      // Filter out built-in themes to avoid duplicates
      return customThemes.filter((t: Theme) => 
        !['default', 'blue', 'purple'].includes(t.id)
      );
    }
  } catch (error) {
    console.error('Failed to load custom themes from localStorage:', error);
  }
  return [];
};

// Helper to load layout from localStorage
const loadLayoutFromStorage = (): WorkspaceLayout => {
  try {
    const stored = localStorage.getItem('music-legend-layout');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load layout from localStorage:', error);
  }
  return defaultLayout;
};

// Helper to load custom layouts from localStorage
const loadCustomLayouts = (): WorkspaceLayout[] => {
  try {
    const stored = localStorage.getItem('music-legend-custom-layouts');
    if (stored) {
      const customLayouts = JSON.parse(stored);
      // Filter out built-in layouts to avoid duplicates
      return customLayouts.filter((l: WorkspaceLayout) => 
        !['default', 'producer', 'performer'].includes(l.id)
      );
    }
  } catch (error) {
    console.error('Failed to load custom layouts from localStorage:', error);
  }
  return [];
};

export const useDAWStore = create<DAWState>((set, get) => {
  // Initialize reduce motion CSS variable on store creation
  if (typeof document !== 'undefined') {
    const reduceMotion = (() => {
      try {
        const stored = localStorage.getItem('music-legend-reduce-motion');
        if (stored !== null) {
          return stored === 'true';
        }
      } catch (error) {
        console.error('Failed to load reduce motion setting:', error);
      }
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    })();
    
    document.documentElement.style.setProperty(
      '--animation-duration',
      reduceMotion ? '0ms' : '300ms'
    );
  }
  
  return {
  // Initial State
  isPlaying: false,
  isRecording: false,
  bpm: 120,
  playMode: 'pattern',
  loopEnabled: true,
  loopStart: 0,
  loopEnd: 4,
  currentTime: 0,
  metronomeEnabled: false,
  
  gridSize: '1/16',
  snapEnabled: true,
  swing: 0,
  
  automationLanes: [],
  automationRecording: false,
  selectedAutomationLane: null,
  
  collaborativeCursors: [],
  comments: [],
  projectSnapshots: (() => {
    // Load snapshots from localStorage on initialization
    try {
      const { loadSnapshotsFromStorage } = require('../services/snapshots');
      return loadSnapshotsFromStorage();
    } catch (error) {
      console.error('Failed to load snapshots from storage:', error);
      return [];
    }
  })(),
  currentUserId: generateId(),
  currentUserName: 'User',
  currentUserColor: '#6366f1',
  
  // Conflict Resolution (Requirements 11.1, 11.2, 11.3)
  conflicts: [],
  conflictNotifications: [],
  
  currentTheme: loadThemeFromStorage(),
  themes: [defaultTheme, blueTheme, purpleTheme, ...loadCustomThemes()],
  currentLayout: loadLayoutFromStorage(),
  layouts: [defaultLayout, producerLayout, performerLayout, ...loadCustomLayouts()],
  
  // UI Panel State (initialized from current layout)
  browserVisible: loadLayoutFromStorage().browserVisible,
  mixerHeight: loadLayoutFromStorage().mixerHeight,
  channelRackHeight: loadLayoutFromStorage().channelRackHeight,
  panelSizes: loadLayoutFromStorage().savedPanelSizes,
  
  // Animation settings (Requirements 14.4, 14.5)
  // Check localStorage first, then fall back to system preference
  reduceMotion: (() => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = localStorage.getItem('music-legend-reduce-motion');
      if (stored !== null) {
        return stored === 'true';
      }
    } catch (error) {
      console.error('Failed to load reduce motion setting from localStorage:', error);
    }
    // Fall back to system preference
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  })(),
  
  // Auto-save settings (Requirements 14.1, 14.2, 14.3, 14.4, 14.5)
  autoSaveEnabled: (() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = localStorage.getItem('music-legend-auto-save-enabled');
      if (stored !== null) {
        return stored === 'true';
      }
    } catch (error) {
      console.error('Failed to load auto-save setting from localStorage:', error);
    }
    return true; // Default to enabled
  })(),
  autoSaveInterval: 30 * 60 * 1000, // 30 minutes in milliseconds
  lastAutoSaveTime: null,
  editingStartTime: null,
  
  projectId: null,
  projectName: 'Untitled Project',
  lastSaved: null,
  
  channels: [],
  currentPattern: createDefaultPattern(),
  patterns: [createDefaultPattern()],
  
  timelineTracks: [
    { id: 'track-1', name: 'Track 1', height: 100, color: '#3b82f6' },
    { id: 'track-2', name: 'Track 2', height: 100, color: '#8b5cf6' },
    { id: 'track-3', name: 'Track 3', height: 100, color: '#ec4899' },
    { id: 'track-4', name: 'Track 4', height: 100, color: '#f59e0b' },
  ],
  patternClips: [],
  audioClips: [],
  pixelsPerBeat: 100,
  currentTool: 'select',
  
  pianoRollNotes: [],
  pianoRollChannel: null,
  pianoRollScale: 'C Major',
  showGhostNotes: false,
  
  mixerTracks: createDefaultMixerTracks(),
  selectedMixerTrack: null,
  masterVolume: 0,
  
  sampleLibrary: [],
  searchQuery: '',
  selectedTags: [],
  
  cpuLoad: 0,
  latency: 0,
  
  delayCompensationEnabled: true,
  maxPluginLatency: 0,
  
  macros: [],
  
  history: {
    past: [],
    future: [],
  },
  
  // Actions
  setPlaying: (playing) => set({ isPlaying: playing }),
  setRecording: (recording) => set({ isRecording: recording }),
  setBpm: (bpm) => {
    Tone.getTransport().bpm.value = bpm;
    set({ bpm });
  },
  setPlayMode: (mode) => set({ playMode: mode }),
  setLoopEnabled: (enabled) => set({ loopEnabled: enabled }),
  setLoopPoints: (start, end) => set({ loopStart: start, loopEnd: end }),
  setCurrentTime: (time) => set({ currentTime: time }),
  toggleMetronome: () => set((state) => ({ metronomeEnabled: !state.metronomeEnabled })),
  
  setGridSize: (size) => set({ gridSize: size }),
  toggleSnap: () => set((state) => ({ snapEnabled: !state.snapEnabled })),
  setSwing: (swing) => {
    Tone.getTransport().swing = swing;
    set({ swing });
  },
  
  addChannel: (channel) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      channels: [...state.channels, { ...channel, id: generateId() }],
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
    // Start editing timer for auto-save (Requirements 14.1)
    get().startEditingTimer();
  },
  updateChannel: (id, updates) => {
    const state = get();
    const channel = state.channels.find(ch => ch.id === id);
    
    // Register edit for conflict detection (Requirements 11.1)
    if (channel) {
      state.registerEdit({
        id: generateId(),
        type: 'channel_edit',
        elementId: id,
        elementName: channel.name,
        data: updates,
      });
    }
    
    set((state) => ({
      channels: state.channels.map((ch) => ch.id === id ? { ...ch, ...updates } : ch),
    }));
    // Start editing timer for auto-save (Requirements 14.1)
    get().startEditingTimer();
  },
  removeChannel: (id) => {
    const state = get();
    const channel = state.channels.find(ch => ch.id === id);
    const snapshot = getSnapshot(state);
    
    // Register delete for conflict detection (Requirements 11.1, 11.4)
    if (channel) {
      state.registerEdit({
        id: generateId(),
        type: 'channel_delete',
        elementId: id,
        elementName: channel.name,
        data: null,
      });
    }
    
    set((state) => ({
      channels: state.channels.filter((ch) => ch.id !== id),
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
    // Start editing timer for auto-save (Requirements 14.1)
    get().startEditingTimer();
  },
  toggleChannelMute: (id) => set((state) => ({
    channels: state.channels.map((ch) => 
      ch.id === id ? { ...ch, muted: !ch.muted } : ch
    ),
  })),
  toggleChannelSolo: (id) => set((state) => ({
    channels: state.channels.map((ch) => 
      ch.id === id ? { ...ch, solo: !ch.solo } : ch
    ),
  })),
  
  toggleStep: (channelIndex, stepIndex) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => {
      const newSteps = [...state.currentPattern.steps];
      newSteps[channelIndex] = [...newSteps[channelIndex]];
      newSteps[channelIndex][stepIndex] = !newSteps[channelIndex][stepIndex];
      return {
        currentPattern: { ...state.currentPattern, steps: newSteps },
        history: {
          past: [...state.history.past, snapshot],
          future: [],
        },
      };
    });
  },
  setStepVelocity: (channelIndex, stepIndex, velocity) => set((state) => {
    const newVelocities = [...state.currentPattern.velocities];
    newVelocities[channelIndex] = [...newVelocities[channelIndex]];
    newVelocities[channelIndex][stepIndex] = velocity;
    return {
      currentPattern: { ...state.currentPattern, velocities: newVelocities },
    };
  }),
  setPatternLength: (length) => set((state) => ({
    currentPattern: { ...state.currentPattern, length },
  })),
  addPattern: (pattern) => set((state) => ({
    patterns: [...state.patterns, { ...pattern, id: generateId() }],
  })),
  selectPattern: (id) => set((state) => {
    const pattern = state.patterns.find((p) => p.id === id);
    return pattern ? { currentPattern: pattern } : {};
  }),
  
  addTimelineTrack: (track) => set((state) => ({
    timelineTracks: [...state.timelineTracks, { ...track, id: generateId() }],
  })),
  addPatternClip: (clip) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      patternClips: [...state.patternClips, { ...clip, id: generateId() }],
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
  },
  updatePatternClip: (id, updates) => {
    const state = get();
    const clip = state.patternClips.find(c => c.id === id);
    
    // Register edit for conflict detection (Requirements 11.1)
    if (clip) {
      state.registerEdit({
        id: generateId(),
        type: 'clip_edit',
        elementId: id,
        elementName: `Clip ${clip.patternId}`,
        data: updates,
      });
    }
    
    set((state) => ({
      patternClips: state.patternClips.map((clip) => 
        clip.id === id ? { ...clip, ...updates } : clip
      ),
    }));
  },
  removePatternClip: (id) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      patternClips: state.patternClips.filter((clip) => clip.id !== id),
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
  },
  addAudioClip: (clip) => set((state) => ({
    audioClips: [...state.audioClips, { ...clip, id: generateId() }],
  })),
  updateAudioClip: (id, updates) => set((state) => ({
    audioClips: state.audioClips.map((clip) => 
      clip.id === id ? { ...clip, ...updates } : clip
    ),
  })),
  setPixelsPerBeat: (pixels) => set({ pixelsPerBeat: pixels }),
  setCurrentTool: (tool) => set({ currentTool: tool }),
  
  addNote: (note) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      pianoRollNotes: [...state.pianoRollNotes, { ...note, id: generateId() }],
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
  },
  updateNote: (id, updates) => {
    const state = get();
    const note = state.pianoRollNotes.find(n => n.id === id);
    
    // Register edit for conflict detection (Requirements 11.1)
    if (note) {
      state.registerEdit({
        id: generateId(),
        type: 'note_edit',
        elementId: id,
        elementName: `Note ${note.pitch}`,
        data: updates,
      });
    }
    
    set((state) => ({
      pianoRollNotes: state.pianoRollNotes.map((note) => 
        note.id === id ? { ...note, ...updates } : note
      ),
    }));
  },
  removeNote: (id) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      pianoRollNotes: state.pianoRollNotes.filter((note) => note.id !== id),
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
  },
  setPianoRollChannel: (channelId) => set({ pianoRollChannel: channelId }),
  setPianoRollScale: (scale) => set({ pianoRollScale: scale }),
  toggleGhostNotes: () => set((state) => ({ showGhostNotes: !state.showGhostNotes })),
  
  updateMixerTrack: (id, updates) => {
    const state = get();
    const track = state.mixerTracks.find(t => t.id === id);
    
    // Register edit for conflict detection (Requirements 11.1)
    if (track) {
      state.registerEdit({
        id: generateId(),
        type: 'mixer_edit',
        elementId: id,
        elementName: track.name,
        data: updates,
      });
    }
    
    set((state) => ({
      mixerTracks: state.mixerTracks.map((track) => 
        track.id === id ? { ...track, ...updates } : track
      ),
    }));
  },
  addEffect: (trackId, effect) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      mixerTracks: state.mixerTracks.map((track) => 
        track.id === trackId 
          ? { ...track, effects: [...track.effects, { ...effect, id: generateId() }] }
          : track
      ),
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
    // Recalculate delay compensation when effects change
    get().recalculateDelayCompensation();
  },
  removeEffect: (trackId, effectId) => {
    const state = get();
    const snapshot = getSnapshot(state);
    set((state) => ({
      mixerTracks: state.mixerTracks.map((track) => 
        track.id === trackId 
          ? { ...track, effects: track.effects.filter((fx) => fx.id !== effectId) }
          : track
      ),
      history: {
        past: [...state.history.past, snapshot],
        future: [],
      },
    }));
    // Recalculate delay compensation when effects change
    get().recalculateDelayCompensation();
  },
  updateEffect: (trackId, effectId, updates) => {
    set((state) => ({
      mixerTracks: state.mixerTracks.map((track) => 
        track.id === trackId 
          ? {
              ...track,
              effects: track.effects.map((fx) => 
                fx.id === effectId ? { ...fx, ...updates } : fx
              ),
            }
          : track
      ),
    }));
    // Recalculate delay compensation when effects change (especially enabled/disabled)
    get().recalculateDelayCompensation();
  },
  setSelectedMixerTrack: (id) => set({ selectedMixerTrack: id }),
  setMasterVolume: (volume) => set({ masterVolume: volume }),
  
  addSample: (sample) => set((state) => ({
    sampleLibrary: [...state.sampleLibrary, sample],
  })),
  setSearchQuery: (query) => set({ searchQuery: query }),
  toggleTag: (tag) => set((state) => ({
    selectedTags: state.selectedTags.includes(tag)
      ? state.selectedTags.filter((t) => t !== tag)
      : [...state.selectedTags, tag],
  })),
  
  updateSystemStats: (cpu, latency) => set({ cpuLoad: cpu, latency }),
  
  // Macro actions
  addMacro: (macro) => set((state) => ({
    macros: [...state.macros, { ...macro, id: generateId() }],
  })),
  removeMacro: (id) => set((state) => ({
    macros: state.macros.filter((m) => m.id !== id),
  })),
  updateMacro: (id, value) => {
    const state = get();
    const macro = state.macros.find((m) => m.id === id);
    if (!macro) return;
    
    // Update macro value
    set((state) => ({
      macros: state.macros.map((m) => 
        m.id === id ? { ...m, value } : m
      ),
    }));
    
    // Update all assigned parameters proportionally
    macro.parameters.forEach((param) => {
      const scaledValue = param.min + (value * (param.max - param.min));
      
      // Update parameter based on parameterId
      // Format: trackId.parameterName or global.parameterName
      const [trackId, paramName] = param.parameterId.split('.');
      
      if (trackId === 'global') {
        // Global parameters
        if (paramName === 'masterVolume') {
          state.setMasterVolume(scaledValue);
        } else if (paramName === 'bpm') {
          state.setBpm(scaledValue);
        }
      } else {
        // Track-specific parameters
        const track = state.mixerTracks.find((t) => t.id === trackId);
        if (track) {
          if (paramName === 'volume') {
            state.updateMixerTrack(trackId, { volume: scaledValue });
          } else if (paramName === 'pan') {
            state.updateMixerTrack(trackId, { pan: scaledValue });
          } else if (paramName === 'eqLow') {
            state.updateMixerTrack(trackId, { eqLow: scaledValue });
          } else if (paramName === 'eqMid') {
            state.updateMixerTrack(trackId, { eqMid: scaledValue });
          } else if (paramName === 'eqHigh') {
            state.updateMixerTrack(trackId, { eqHigh: scaledValue });
          } else if (paramName === 'sendA') {
            state.updateMixerTrack(trackId, { sendA: scaledValue });
          } else if (paramName === 'sendB') {
            state.updateMixerTrack(trackId, { sendB: scaledValue });
          } else if (paramName === 'stereoWidth') {
            state.updateMixerTrack(trackId, { stereoWidth: scaledValue });
          }
        }
      }
    });
  },
  updateMacroName: (id, name) => set((state) => ({
    macros: state.macros.map((m) => 
      m.id === id ? { ...m, name } : m
    ),
  })),
  addMacroParameter: (macroId, parameter) => set((state) => ({
    macros: state.macros.map((m) => 
      m.id === macroId 
        ? { ...m, parameters: [...m.parameters, parameter] }
        : m
    ),
  })),
  removeMacroParameter: (macroId, parameterId) => set((state) => ({
    macros: state.macros.map((m) => 
      m.id === macroId 
        ? { ...m, parameters: m.parameters.filter((p) => p.parameterId !== parameterId) }
        : m
    ),
  })),
  updateMacroParameter: (macroId, parameterId, updates) => set((state) => ({
    macros: state.macros.map((m) => 
      m.id === macroId 
        ? {
            ...m,
            parameters: m.parameters.map((p) => 
              p.parameterId === parameterId ? { ...p, ...updates } : p
            ),
          }
        : m
    ),
  })),
  
  // History management
  undo: () => {
    const state = get();
    if (state.history.past.length === 0) return;
    
    const previous = state.history.past[state.history.past.length - 1];
    const newPast = state.history.past.slice(0, state.history.past.length - 1);
    
    set({
      ...previous,
      history: {
        past: newPast,
        future: [getSnapshot(state), ...state.history.future],
      },
    });
  },
  
  redo: () => {
    const state = get();
    if (state.history.future.length === 0) return;
    
    const next = state.history.future[0];
    const newFuture = state.history.future.slice(1);
    
    set({
      ...next,
      history: {
        past: [...state.history.past, getSnapshot(state)],
        future: newFuture,
      },
    });
  },
  
  canUndo: () => get().history.past.length > 0,
  canRedo: () => get().history.future.length > 0,
  
  // Automation actions
  addAutomationLane: (lane) => set((state) => ({
    automationLanes: [...state.automationLanes, { ...lane, id: generateId() }],
  })),
  removeAutomationLane: (id) => set((state) => ({
    automationLanes: state.automationLanes.filter((lane) => lane.id !== id),
  })),
  addAutomationPoint: (laneId, point) => set((state) => ({
    automationLanes: state.automationLanes.map((lane) =>
      lane.id === laneId
        ? { ...lane, points: [...lane.points, point].sort((a, b) => a.time - b.time) }
        : lane
    ),
  })),
  removeAutomationPoint: (laneId, time) => set((state) => ({
    automationLanes: state.automationLanes.map((lane) =>
      lane.id === laneId
        ? { ...lane, points: lane.points.filter((p) => Math.abs(p.time - time) > 0.01) }
        : lane
    ),
  })),
  updateAutomationPoint: (laneId, time, value) => set((state) => ({
    automationLanes: state.automationLanes.map((lane) =>
      lane.id === laneId
        ? {
            ...lane,
            points: lane.points.map((p) =>
              Math.abs(p.time - time) < 0.01 ? { ...p, value } : p
            ),
          }
        : lane
    ),
  })),
  toggleAutomationRecording: () => set((state) => ({
    automationRecording: !state.automationRecording,
  })),
  setSelectedAutomationLane: (id) => set({ selectedAutomationLane: id }),
  
  // Collaboration actions
  updateCollaborativeCursor: (cursor) => set((state) => {
    const existingIndex = state.collaborativeCursors.findIndex(
      (c) => c.userId === cursor.userId
    );
    if (existingIndex >= 0) {
      const updated = [...state.collaborativeCursors];
      updated[existingIndex] = cursor;
      return { collaborativeCursors: updated };
    }
    return { collaborativeCursors: [...state.collaborativeCursors, cursor] };
  }),
  removeCollaborativeCursor: (userId) => set((state) => ({
    collaborativeCursors: state.collaborativeCursors.filter((c) => c.userId !== userId),
  })),
  addComment: (comment) => {
    const state = get();
    const newComment = {
      ...comment,
      id: generateId(),
      author: state.currentUserName,
      timestamp: Date.now(),
      resolved: false,
    };
    
    set((state) => ({
      comments: [...state.comments, newComment],
    }));
    
    // Broadcast comment_added via WebSocket (Requirements 5.1)
    // The WebSocket client will be accessed from a React component/hook
    // Store the operation in a queue that will be picked up by the WebSocket service
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('comment-operation', {
        detail: {
          type: 'comment_added',
          projectId: state.projectId,
          comment: newComment,
        },
      }));
    }
  },
  updateComment: (id, updates) => {
    const state = get();
    const updatedComment = state.comments.find((c) => c.id === id);
    
    set((state) => ({
      comments: state.comments.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
    
    // Broadcast comment_updated via WebSocket (Requirements 5.2)
    if (updatedComment && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('comment-operation', {
        detail: {
          type: 'comment_updated',
          projectId: state.projectId,
          comment: { ...updatedComment, ...updates },
        },
      }));
    }
  },
  removeComment: (id) => {
    const state = get();
    const commentToRemove = state.comments.find((c) => c.id === id);
    
    set((state) => ({
      comments: state.comments.filter((c) => c.id !== id),
    }));
    
    // Broadcast comment_deleted via WebSocket (Requirements 5.2)
    if (commentToRemove && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('comment-operation', {
        detail: {
          type: 'comment_deleted',
          projectId: state.projectId,
          comment: commentToRemove,
        },
      }));
    }
  },
  createSnapshot: (name, description) => {
    const state = get();
    
    // Check snapshot limit - Requirements 6.4
    if (state.projectSnapshots.length >= 50) {
      console.error('Maximum of 50 snapshots reached');
      return;
    }
    
    const snapshot: ProjectSnapshot = {
      id: generateId(),
      name,
      timestamp: Date.now(),
      description,
      state: {
        channels: state.channels,
        patterns: state.patterns,
        currentPattern: state.currentPattern,
        patternClips: state.patternClips,
        audioClips: state.audioClips,
        pianoRollNotes: state.pianoRollNotes,
        mixerTracks: state.mixerTracks,
        automationLanes: state.automationLanes,
        bpm: state.bpm,
      },
    };
    
    set((state) => ({
      projectSnapshots: [...state.projectSnapshots, snapshot],
    }));
    
    // Save to localStorage with compression - Requirements 6.3, 15.5
    try {
      const { saveSnapshotsToStorage } = require('../services/snapshots');
      saveSnapshotsToStorage(get().projectSnapshots);
    } catch (error) {
      console.error('Failed to save snapshot to storage:', error);
    }
    
    // Reset editing timer after manual snapshot (Requirements 14.1)
    // Only reset if this is not an auto-save snapshot
    if (!name.startsWith('Auto-save [')) {
      get().resetEditingTimer();
    }
  },
  loadSnapshot: (id) => {
    const state = get();
    const snapshot = state.projectSnapshots.find((s) => s.id === id);
    if (snapshot) {
      set({ ...snapshot.state });
    }
  },
  deleteSnapshot: (id) => {
    set((state) => ({
      projectSnapshots: state.projectSnapshots.filter((s) => s.id !== id),
    }));
    
    // Update localStorage - Requirements 7.5
    try {
      const { saveSnapshotsToStorage } = require('../services/snapshots');
      saveSnapshotsToStorage(get().projectSnapshots);
    } catch (error) {
      console.error('Failed to update storage after deletion:', error);
    }
  },
  
  // Theme actions
  setTheme: (theme) => {
    set({ currentTheme: theme });
    // Apply theme to CSS variables
    applyThemeVariables(theme);
    // Persist to localStorage
    try {
      localStorage.setItem('music-legend-theme', JSON.stringify(theme));
    } catch (error) {
      console.error('Failed to save theme to localStorage:', error);
    }
    // Sync to backend when authenticated (Requirements 9.4)
    syncThemeToBackend(theme).catch(err => {
      console.error('Backend sync failed:', err);
    });
  },
  addCustomTheme: (theme) => {
    set((state) => ({
      themes: [...state.themes, theme],
    }));
    // Persist custom themes to localStorage (exclude built-in themes)
    try {
      const state = get();
      const customThemes = state.themes.filter((t) => 
        !['default', 'blue', 'purple'].includes(t.id)
      );
      localStorage.setItem('music-legend-custom-themes', JSON.stringify(customThemes));
      // Sync to backend when authenticated (Requirements 9.4)
      syncCustomThemesToBackend(customThemes).catch(err => {
        console.error('Backend sync failed:', err);
      });
    } catch (error) {
      console.error('Failed to save custom themes to localStorage:', error);
    }
  },
  
  exportTheme: (themeId) => {
    const state = get();
    const theme = state.themes.find((t) => t.id === themeId);
    if (!theme) {
      console.error('Theme not found:', themeId);
      return;
    }
    
    // Create export data with metadata (Requirements 11.2)
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      theme: {
        ...theme,
        // Add metadata
        metadata: {
          exportedBy: 'Music Legend Insiders',
          originalId: theme.id,
        },
      },
    };
    
    // Convert to JSON string
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Create blob and download (Requirements 11.1)
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${theme.name.replace(/\s+/g, '-').toLowerCase()}-theme.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  
  importTheme: async (file: File) => {
    try {
      // Read file content
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate theme structure (Requirements 11.4)
      if (!data.theme || !data.theme.name || !data.theme.colors) {
        throw new Error('Invalid theme file: missing required fields (name, colors)');
      }
      
      const theme = data.theme;
      
      // Validate colors object
      const requiredColors = [
        'primary', 'secondary', 'tertiary', 'elevated',
        'border', 'borderLight', 'accentPrimary', 'accentHover', 'accentActive'
      ];
      
      for (const color of requiredColors) {
        if (!theme.colors[color]) {
          throw new Error(`Invalid theme file: missing color "${color}"`);
        }
        // Validate color format (hex color)
        if (!/^#[0-9A-Fa-f]{6}$/.test(theme.colors[color])) {
          throw new Error(`Invalid theme file: invalid color format for "${color}"`);
        }
      }
      
      // Generate new ID to avoid conflicts
      const importedTheme: Theme = {
        id: generateId(),
        name: theme.name,
        colors: {
          primary: theme.colors.primary,
          secondary: theme.colors.secondary,
          tertiary: theme.colors.tertiary,
          elevated: theme.colors.elevated,
          border: theme.colors.border,
          borderLight: theme.colors.borderLight,
          accentPrimary: theme.colors.accentPrimary,
          accentHover: theme.colors.accentHover,
          accentActive: theme.colors.accentActive,
        },
      };
      
      // Add to themes list (Requirements 11.4)
      get().addCustomTheme(importedTheme);
      
      console.log('Theme imported successfully:', importedTheme.name);
    } catch (error) {
      // Display error message (Requirements 11.5)
      console.error('Failed to import theme:', error);
      throw error; // Re-throw so UI can display error
    }
  },
  
  removeCustomTheme: (themeId) => {
    const state = get();
    // Prevent removing built-in themes
    if (['default', 'blue', 'purple'].includes(themeId)) {
      console.error('Cannot remove built-in theme');
      return;
    }
    
    set((state) => ({
      themes: state.themes.filter((t) => t.id !== themeId),
    }));
    
    // Update localStorage
    try {
      const updatedState = get();
      const customThemes = updatedState.themes.filter((t) => 
        !['default', 'blue', 'purple'].includes(t.id)
      );
      localStorage.setItem('music-legend-custom-themes', JSON.stringify(customThemes));
      // Sync to backend when authenticated
      syncCustomThemesToBackend(customThemes).catch(err => {
        console.error('Backend sync failed:', err);
      });
    } catch (error) {
      console.error('Failed to update custom themes in localStorage:', error);
    }
    
    // If the deleted theme was active, switch to default
    if (state.currentTheme.id === themeId) {
      get().setTheme(defaultTheme);
    }
  },
  
  // Workspace actions
  setLayout: (layout) => {
    set({ 
      currentLayout: layout,
      // Update UI panel state to match layout (Requirements 6.2)
      browserVisible: layout.browserVisible,
      mixerHeight: layout.mixerHeight,
      channelRackHeight: layout.channelRackHeight,
      panelSizes: layout.savedPanelSizes,
    });
    // Persist to localStorage
    try {
      localStorage.setItem('music-legend-layout', JSON.stringify(layout));
    } catch (error) {
      console.error('Failed to save layout to localStorage:', error);
    }
    // Sync to backend when authenticated (Requirements 9.4)
    syncLayoutToBackend(layout).catch(err => {
      console.error('Backend sync failed:', err);
    });
  },
  saveCurrentLayout: (name) => {
    const state = get();
    // Capture current layout state (Requirements 6.2, 6.4)
    const layout: WorkspaceLayout = {
      id: generateId(),
      name,
      browserVisible: state.browserVisible,
      mixerHeight: state.mixerHeight,
      channelRackHeight: state.channelRackHeight,
      savedPanelSizes: { ...state.panelSizes },
    };
    set((state) => ({
      layouts: [...state.layouts, layout],
      currentLayout: layout,
    }));
    // Persist custom layouts to localStorage (Requirements 6.3)
    try {
      const updatedState = get();
      const customLayouts = updatedState.layouts.filter((l) => 
        !['default', 'producer', 'performer'].includes(l.id)
      );
      localStorage.setItem('music-legend-custom-layouts', JSON.stringify(customLayouts));
      localStorage.setItem('music-legend-layout', JSON.stringify(layout));
      // Sync to backend when authenticated (Requirements 6.5, 9.4)
      syncCustomLayoutsToBackend(customLayouts).catch(err => {
        console.error('Backend sync failed:', err);
      });
      syncLayoutToBackend(layout).catch(err => {
        console.error('Backend sync failed:', err);
      });
    } catch (error) {
      console.error('Failed to save custom layouts to localStorage:', error);
    }
  },
  deleteLayout: (id) => {
    set((state) => ({
      layouts: state.layouts.filter((l) => l.id !== id),
    }));
    // Update localStorage
    try {
      const state = get();
      const customLayouts = state.layouts.filter((l) => 
        !['default', 'producer', 'performer'].includes(l.id)
      );
      localStorage.setItem('music-legend-custom-layouts', JSON.stringify(customLayouts));
      // Sync to backend when authenticated (Requirements 9.4)
      syncCustomLayoutsToBackend(customLayouts).catch(err => {
        console.error('Backend sync failed:', err);
      });
    } catch (error) {
      console.error('Failed to update custom layouts in localStorage:', error);
    }
  },
  
  // UI Panel State actions
  setBrowserVisible: (visible) => set({ browserVisible: visible }),
  setMixerHeight: (height) => set({ mixerHeight: height }),
  setChannelRackHeight: (height) => set({ channelRackHeight: height }),
  setPanelSize: (panelId, size) => set((state) => ({
    panelSizes: { ...state.panelSizes, [panelId]: size },
  })),
  
  // Animation settings actions (Requirements 14.4, 14.5)
  setReduceMotion: (enabled) => {
    set({ reduceMotion: enabled });
    // Update CSS custom property for animations
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty(
        '--animation-duration',
        enabled ? '0ms' : '300ms'
      );
    }
    // Persist to localStorage (Requirements 14.5)
    try {
      localStorage.setItem('music-legend-reduce-motion', enabled.toString());
    } catch (error) {
      console.error('Failed to save reduce motion setting to localStorage:', error);
    }
  },
  
  // Auto-save actions (Requirements 14.1, 14.2, 14.3, 14.4, 14.5)
  setAutoSaveEnabled: (enabled) => {
    set({ autoSaveEnabled: enabled });
    // Persist to localStorage (Requirements 14.5)
    try {
      localStorage.setItem('music-legend-auto-save-enabled', enabled.toString());
    } catch (error) {
      console.error('Failed to save auto-save setting to localStorage:', error);
    }
    // Reset timer if disabled
    if (!enabled) {
      set({ editingStartTime: null, lastAutoSaveTime: null });
    }
  },
  
  setAutoSaveInterval: (interval) => {
    set({ autoSaveInterval: interval });
  },
  
  // Start tracking editing time (Requirements 14.1)
  startEditingTimer: () => {
    const state = get();
    if (!state.editingStartTime) {
      set({ editingStartTime: Date.now() });
    }
  },
  
  // Reset editing timer after save (Requirements 14.1)
  resetEditingTimer: () => {
    set({ 
      editingStartTime: Date.now(),
      lastAutoSaveTime: Date.now()
    });
  },
  
  // Check if auto-save should trigger (Requirements 14.1, 14.2, 14.3)
  checkAutoSave: () => {
    const state = get();
    
    // Don't auto-save if disabled (Requirements 14.5)
    if (!state.autoSaveEnabled) {
      return;
    }
    
    // Don't auto-save if no editing has occurred
    if (!state.editingStartTime) {
      return;
    }
    
    const now = Date.now();
    const editingDuration = now - state.editingStartTime;
    
    // Check if 30 minutes have passed (Requirements 14.1)
    if (editingDuration >= state.autoSaveInterval) {
      // Create auto-save snapshot
      const timestamp = new Date().toLocaleString();
      const autoSaveName = `Auto-save [${timestamp}]`;
      
      // Get current auto-save snapshots (Requirements 14.2)
      const autoSaveSnapshots = state.projectSnapshots.filter(s => 
        s.name.startsWith('Auto-save [')
      );
      
      // Delete oldest auto-save if limit reached (Requirements 14.3)
      if (autoSaveSnapshots.length >= 10) {
        const oldestAutoSave = autoSaveSnapshots.sort((a, b) => a.timestamp - b.timestamp)[0];
        get().deleteSnapshot(oldestAutoSave.id);
      }
      
      // Create the auto-save snapshot
      get().createSnapshot(autoSaveName);
      
      // Reset timer (Requirements 14.1)
      get().resetEditingTimer();
      
      console.log('Auto-save snapshot created:', autoSaveName);
    }
  },
  
  // Project actions
  setProjectName: (name) => set({ projectName: name }),
  saveProject: async () => {
    // TODO: Implement project saving when backend is ready
    set({ lastSaved: Date.now() });
    console.log('Project save not yet implemented');
  },
  loadProject: async (projectId: string) => {
    // TODO: Implement project loading when backend is ready
    console.log('Project load not yet implemented', projectId);
    
    // Load comments from backend (Requirements 5.4)
    try {
      // This will be implemented when backend is ready
      // For now, just set the project ID
      set({ projectId });
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  },
  
  // Preferences sync action
  loadPreferencesFromBackend: async () => {
    try {
      const preferences = await loadPreferencesFromBackend();
      if (preferences) {
        // Apply theme from backend (Requirements 9.5)
        if (preferences.theme) {
          set({ currentTheme: preferences.theme });
          applyThemeVariables(preferences.theme);
        }
        
        // Apply layout from backend (Requirements 9.5)
        if (preferences.layout) {
          set({ currentLayout: preferences.layout });
        }
        
        // Merge custom themes (Requirements 9.5)
        if (preferences.customThemes && preferences.customThemes.length > 0) {
          const builtInThemes = [defaultTheme, blueTheme, purpleTheme];
          const mergedThemes = [...builtInThemes, ...preferences.customThemes];
          set({ themes: mergedThemes });
        }
        
        // Merge custom layouts (Requirements 9.5)
        if (preferences.customLayouts && preferences.customLayouts.length > 0) {
          const builtInLayouts = [defaultLayout, producerLayout, performerLayout];
          const mergedLayouts = [...builtInLayouts, ...preferences.customLayouts];
          set({ layouts: mergedLayouts });
        }
        
        console.log('Preferences loaded from backend and applied');
      }
    } catch (error) {
      console.error('Failed to load preferences from backend:', error);
    }
  },
  
  // Plugin Delay Compensation actions
  setDelayCompensationEnabled: (enabled) => {
    set({ delayCompensationEnabled: enabled });
    if (enabled) {
      get().recalculateDelayCompensation();
    } else {
      // Reset all delay compensation when disabled
      set((state) => ({
        mixerTracks: state.mixerTracks.map((track) => ({
          ...track,
          delayCompensation: 0,
        })),
        maxPluginLatency: 0,
      }));
    }
  },
  
  updatePluginLatency: (trackId, effectId, latency) => {
    set((state) => ({
      mixerTracks: state.mixerTracks.map((track) =>
        track.id === trackId
          ? {
              ...track,
              effects: track.effects.map((fx) =>
                fx.id === effectId ? { ...fx, latency } : fx
              ),
            }
          : track
      ),
    }));
    // Recalculate delay compensation after updating latency
    get().recalculateDelayCompensation();
  },
  
  recalculateDelayCompensation: () => {
    const state = get();
    if (!state.delayCompensationEnabled) return;
    
    // Calculate total latency for each track (sum of all enabled effects)
    const trackLatencies = state.mixerTracks.map((track) => {
      const totalLatency = track.effects
        .filter((fx) => fx.enabled)
        .reduce((sum, fx) => sum + (fx.latency || 0), 0);
      return { trackId: track.id, latency: totalLatency };
    });
    
    // Find maximum latency across all tracks
    const maxLatency = Math.max(...trackLatencies.map((t) => t.latency), 0);
    
    // Calculate delay compensation for each track
    // Tracks with less latency need to be delayed to match the maximum
    set((state) => ({
      maxPluginLatency: maxLatency,
      mixerTracks: state.mixerTracks.map((track) => {
        const trackLatency = trackLatencies.find((t) => t.trackId === track.id);
        const compensation = maxLatency - (trackLatency?.latency || 0);
        return { ...track, delayCompensation: compensation };
      }),
    }));
  },
  
  // Conflict Resolution actions (Requirements 11.1, 11.2, 11.3)
  
  /**
   * Register a local edit operation
   * Detects conflicts with recent edits from other users
   */
  registerEdit: (operation) => {
    const state = get();
    
    // Create full operation with current user info
    const fullOperation: EditOperation = {
      ...operation,
      userId: state.currentUserId,
      userName: state.currentUserName,
      timestamp: Date.now(),
    };
    
    // Register with conflict detector (Requirements 11.1)
    const conflict = conflictDetector.registerEdit(fullOperation);
    
    if (conflict) {
      // Add conflict to state (Requirements 11.3)
      set((state) => ({
        conflicts: [...state.conflicts, conflict],
      }));
      
      // Create notification (Requirements 11.2)
      const notification = createConflictNotification(conflict);
      set((state) => ({
        conflictNotifications: [...state.conflictNotifications, notification],
      }));
      
      // Check if manual resolution required (Requirements 11.4)
      if (requiresManualResolution(conflict)) {
        // Dispatch event for UI to show manual resolution dialog
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('conflict-manual-resolution', {
            detail: conflict,
          }));
        }
      }
    }
  },
  
  /**
   * Handle edit operation from remote user
   * Applies last-write-wins strategy for conflict resolution
   */
  handleRemoteEdit: (remoteOperation) => {
    const state = get();
    
    // Register the remote edit to detect conflicts
    const conflict = conflictDetector.registerEdit(remoteOperation);
    
    if (conflict) {
      // Conflict detected - add to state (Requirements 11.3)
      set((state) => ({
        conflicts: [...state.conflicts, conflict],
      }));
      
      // Create notification (Requirements 11.2)
      const notification = createConflictNotification(conflict);
      set((state) => ({
        conflictNotifications: [...state.conflictNotifications, notification],
      }));
      
      console.log('Remote edit conflict:', conflict);
    }
    
    // Apply the remote operation using last-write-wins (Requirements 11.1)
    // The remote operation is always applied since it came from the server
    // which has already resolved conflicts
    
    switch (remoteOperation.type) {
      case 'channel_edit':
        if (remoteOperation.data) {
          state.updateChannel(remoteOperation.elementId, remoteOperation.data);
        }
        break;
        
      case 'channel_delete':
        state.removeChannel(remoteOperation.elementId);
        break;
        
      case 'pattern_edit':
        // Apply pattern changes
        if (remoteOperation.data) {
          const pattern = state.patterns.find(p => p.id === remoteOperation.elementId);
          if (pattern) {
            set((state) => ({
              patterns: state.patterns.map(p => 
                p.id === remoteOperation.elementId 
                  ? { ...p, ...remoteOperation.data }
                  : p
              ),
            }));
          }
        }
        break;
        
      case 'mixer_edit':
        if (remoteOperation.data) {
          state.updateMixerTrack(remoteOperation.elementId, remoteOperation.data);
        }
        break;
        
      case 'note_edit':
        if (remoteOperation.data) {
          state.updateNote(remoteOperation.elementId, remoteOperation.data);
        }
        break;
        
      case 'automation_edit':
        if (remoteOperation.data) {
          const lane = state.automationLanes.find(l => l.id === remoteOperation.elementId);
          if (lane) {
            set((state) => ({
              automationLanes: state.automationLanes.map(l =>
                l.id === remoteOperation.elementId
                  ? { ...l, ...remoteOperation.data }
                  : l
              ),
            }));
          }
        }
        break;
        
      case 'clip_edit':
        if (remoteOperation.data) {
          state.updatePatternClip(remoteOperation.elementId, remoteOperation.data);
        }
        break;
        
      default:
        console.warn('Unknown remote operation type:', remoteOperation.type);
    }
  },
  
  /**
   * Get recent conflicts for activity feed (Requirements 11.3)
   */
  getRecentConflicts: () => {
    return conflictDetector.getRecentConflicts();
  },
  
  /**
   * Clear conflict history
   */
  clearConflicts: () => {
    conflictDetector.clearConflicts();
    set({ conflicts: [], conflictNotifications: [] });
  },
  
  /**
   * Dismiss a conflict notification (Requirements 11.2)
   */
  dismissConflictNotification: (index) => {
    set((state) => ({
      conflictNotifications: state.conflictNotifications.filter((_, i) => i !== index),
    }));
  },
};
});
